import environmentHelper from '../utils/environmentHelper';

export interface IOAuthTokens {
    id_token: string;
    access_token: string;
    refresh_token: string;
}

const VERIFIER_KEY = 'cognito_oauth_verifier';
const STATE_KEY = 'cognito_oauth_state';
const REDIRECT_KEY = 'cognito_oauth_redirect';

function base64UrlEncode(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...Array.from(bytes)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function generateCodeVerifier(): Promise<string> {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}

function randomState(): string {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}

export const cognitoOAuth = {
    async buildAuthUrl(): Promise<string> {
        const { clientId, domain, redirectUri } = environmentHelper.getCognitoOAuthConfig();
        const verifier = await generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        const state = randomState();
        sessionStorage.setItem(VERIFIER_KEY, verifier);
        sessionStorage.setItem(STATE_KEY, state);

        const params = new URLSearchParams();
        params.append('response_type', 'code');
        params.append('client_id', clientId);
        params.append('redirect_uri', redirectUri);
        params.append('scope', 'openid email profile');
        params.append('code_challenge', challenge);
        params.append('code_challenge_method', 'S256');
        params.append('state', state);
        return `https://${domain}/oauth2/authorize?${params}`;
    },

    // Cognito hosted-UI logout: clears the managed-login SSO cookie so the next
    // "Sign in with Cognito" prompts for credentials instead of silently reusing the
    // session. logout_uri must be a registered Logout URL on the app client.
    buildLogoutUrl(): string {
        const { clientId, domain } = environmentHelper.getCognitoOAuthConfig();
        const logoutUri = `${window.location.origin}/login`;
        const params = new URLSearchParams();
        params.append('client_id', clientId);
        params.append('logout_uri', logoutUri);
        return `https://${domain}/logout?${params}`;
    },

    // CSRF protection: the `state` Cognito returns on the callback must match the
    // value we generated and stashed before redirecting. Single-use (cleared on read),
    // so a replayed or forged callback without the matching state is rejected.
    validateState(returnedState: string | null): boolean {
        const stored = sessionStorage.getItem(STATE_KEY);
        sessionStorage.removeItem(STATE_KEY);
        return !!stored && stored === returnedState;
    },

    // Remember where to send the user after the hosted-UI round-trip (we leave the
    // SPA, so router/redux state is lost). Only in-app paths are stored/returned —
    // guards against open-redirect to an external URL.
    setPostLoginRedirect(path?: string) {
        if (path && path.startsWith('/') && !path.startsWith('//')) {
            sessionStorage.setItem(REDIRECT_KEY, path);
        } else {
            sessionStorage.removeItem(REDIRECT_KEY);
        }
    },

    consumePostLoginRedirect(): string | null {
        const path = sessionStorage.getItem(REDIRECT_KEY);
        sessionStorage.removeItem(REDIRECT_KEY);
        return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
    },

    async exchangeCodeForTokens(code: string): Promise<IOAuthTokens> {
        const { clientId, domain, redirectUri } = environmentHelper.getCognitoOAuthConfig();
        const verifier = sessionStorage.getItem(VERIFIER_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);

        if (!verifier) {
            throw new Error('OAuth state missing — please try signing in again.');
        }

        const body = new URLSearchParams();
        body.append('grant_type', 'authorization_code');
        body.append('client_id', clientId);
        body.append('redirect_uri', redirectUri);
        body.append('code', code);
        body.append('code_verifier', verifier);

        const response = await fetch(`https://${domain}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Token exchange failed: ${text}`);
        }

        return response.json();
    },

    // Refresh an OAuth session via the refresh-token grant. Cognito's refresh grant
    // returns a new id_token + access_token but NOT a new refresh_token, so carry the
    // existing one forward. The OAuth app client is public (no secret), so this is
    // just client_id + refresh_token.
    async refreshTokens(refreshToken: string): Promise<IOAuthTokens> {
        const { clientId, domain } = environmentHelper.getCognitoOAuthConfig();
        const body = new URLSearchParams();
        body.append('grant_type', 'refresh_token');
        body.append('client_id', clientId);
        body.append('refresh_token', refreshToken);

        const response = await fetch(`https://${domain}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Token refresh failed: ${text}`);
        }

        const data = await response.json();
        return {
            id_token: data.id_token,
            access_token: data.access_token,
            refresh_token: data.refresh_token || refreshToken,
        };
    },

    // Wraps raw JWT strings in an object shaped like CognitoUserSession so that
    // the existing completeLogin action (which calls runPostLogin) works unchanged.
    buildSessionAdapter(tokens: IOAuthTokens) {
        const makeToken = (jwt: string) => {
            // JWT uses base64url (- and _ instead of + and /), no padding.
            // atob() requires standard base64 with padding.
            const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64 + '=='.slice(0, (4 - base64.length % 4) % 4);
            const payload = JSON.parse(atob(padded));
            return {
                jwtToken: jwt,
                payload,
                getJwtToken: () => jwt,
                decodePayload: () => payload,
            };
        };

        return {
            getIdToken:      () => makeToken(tokens.id_token),
            getAccessToken:  () => makeToken(tokens.access_token),
            getRefreshToken: () => ({ token: tokens.refresh_token }),
            // Reflect real token expiry (exp is seconds since epoch) instead of
            // always-valid, so callers can tell when a refresh is needed.
            isValid:         () => {
                try {
                    const { exp } = makeToken(tokens.id_token).payload;
                    return typeof exp === 'number' && exp * 1000 > Date.now();
                } catch (e) {
                    return false;
                }
            },
        };
    },
};
